import { ArrowDownRight } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";

const HERO_BG =
  "https://static.prod-images.emergentagent.com/jobs/4df06464-aefb-41c6-a541-b339198b5cfe/images/43c77cee5244bc80098229a485b7e5e0c2cc28d91db76fe9a4ee6643ddb82782.png";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_website-builder-1674/artifacts/923t2kyx_image.png";

export default function Hero() {
  const { t } = useT();
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] overflow-hidden border-b border-white/10"
    >
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url('${HERO_BG}')` }}
        aria-hidden
      />
      <div className="absolute inset-0 bg-black/65" aria-hidden />
      <div className="absolute inset-0 grid-line opacity-40" aria-hidden />

      <div className="relative grid grid-cols-12 gap-0 min-h-[100svh] pt-28 sm:pt-32">
        <div className="col-span-12 px-6 sm:px-10 py-10 md:py-16 flex flex-col justify-between">
          {/* Top: Logo + eyebrow text side-by-side */}
          <div className="reveal">
            <div className="flex items-center gap-6 sm:gap-10 mb-12">
              <img
                src={LOGO_URL}
                alt="S&K Facade Panels"
                data-testid="hero-logo"
                className="h-32 sm:h-40 md:h-52 w-auto object-contain shrink-0"
                style={{ mixBlendMode: "lighten" }}
              />
              <div className="flex flex-col gap-3">
                <p
                  className="text-xs uppercase tracking-[0.35em] text-white/70"
                  data-testid="hero-eyebrow"
                >
                  {t.hero.eyebrow}
                </p>
                <p
                  className="text-[10px] uppercase tracking-[0.3em] text-white/45 inline-flex items-center gap-2"
                  data-testid="hero-sub-eyebrow"
                >
                  <span className="inline-block w-6 h-px bg-white/40" />
                  {t.hero.subEyebrow}
                </p>
              </div>
            </div>

            <h1
              data-testid="hero-headline"
              className="font-display uppercase tracking-tighter text-white text-[12vw] md:text-[8.5vw] leading-[0.92]"
            >
              <span className="font-black">{t.hero.headlineBold}</span>{" "}
              <span className="font-light italic">{t.hero.headlineRest}</span>
            </h1>
          </div>

          <div
            className="grid grid-cols-12 gap-6 mt-12 md:mt-20 reveal"
            style={{ animationDelay: "200ms" }}
          >
            <div className="col-span-12 md:col-span-6">
              <p className="text-sm md:text-base text-white/70 leading-relaxed">
                {t.hero.description}
              </p>
            </div>
            <div className="hidden md:flex col-span-1" />
            <div className="col-span-12 md:col-span-5 flex md:justify-end items-end">
              <a
                href="#finishes"
                data-testid="hero-cta-explore"
                className="inline-flex items-center gap-3 border border-white/20 hover:border-white/60 px-5 py-3 text-xs uppercase tracking-[0.25em] text-white transition-colors"
              >
                {t.hero.cta}
                <ArrowDownRight size={16} strokeWidth={1.5} />
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="relative border-t border-white/10 bg-black/40 backdrop-blur-md overflow-hidden">
        <div className="flex marquee-track whitespace-nowrap py-4 text-xs uppercase tracking-[0.35em] text-white/50">
          {Array.from({ length: 2 }).map((_, k) => (
            <div key={k} className="flex shrink-0 gap-12 pr-12">
              {t.hero.marquee.flatMap((item, i) => [
                <span key={`i-${k}-${i}`}>{item}</span>,
                <span key={`d-${k}-${i}`}>·</span>,
              ])}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
